import { getSupabaseClient } from '../../../../core/supabase/client.js';

export class SupabaseUploadRemoteDataSource {
  async upload(
    bucket: string,
    remotePath: string,
    file: File | Blob,
    mimeType: string,
    onProgress: (progress: number) => void,
  ): Promise<string> {
    const client = getSupabaseClient();
    onProgress(0);

    const { error } = await client.storage.from(bucket).upload(remotePath, file, {
      contentType: mimeType,
      upsert: true,
    });

    if (error) throw error;

    onProgress(1);

    const { data: signedData, error: signedError } = await client.storage
      .from(bucket)
      .createSignedUrl(remotePath, 60 * 60 * 24 * 365);

    if (signedError) throw signedError;
    return signedData.signedUrl;
  }

  async delete(bucket: string, path: string): Promise<void> {
    const client = getSupabaseClient();
    const { error } = await client.storage.from(bucket).remove([path]);
    if (error) throw error;
  }

  /** Resolve a media folder using media_folder_build — use when you have org/project/location UUIDs directly. */
  async getMediaFolderBuild(args: MediaFolderBuildArgs): Promise<string | null> {
    const client = getSupabaseClient() as any;
    const { data, error } = await client.rpc('media_folder_build', {
      p_org_id: args.orgId,
      p_project_id: args.projectId,
      p_location_id: args.locationId,
      p_when: null,
      p_feature: args.feature,
    });
    if (error) throw new Error(error.message);
    return (data as string | null) ?? null;
  }

  /** Resolve the canonical media folder server-side (single source of truth). */
  async getMediaFolder(args: MediaFolderArgs): Promise<string | null> {
    const client = getSupabaseClient() as any;
    const { data, error } = await client.rpc('get_media_folder', {
      p_mode: args.mode,
      p_session_id: args.sessionId ?? null,
      p_field_id: args.fieldId ?? null,
      p_location_id: args.locationId ?? null,
      p_project_id: args.projectId ?? null,
      p_org_id: args.orgId ?? null,
      p_transaction_id: args.transactionId ?? null,
    });
    if (error) throw new Error(error.message);
    return (data as string | null) ?? null;
  }
}

export type MediaFolderMode = 'formx' | 'checkin' | 'checkout' | 'payout' | 'sale' | 'issue' | 'stock_transaction';

export interface MediaFolderArgs {
  mode: MediaFolderMode;
  sessionId?: string | null;
  /** Required for mode='formx'; backend resolves form name and field name from this id. */
  fieldId?: string | null;
  /** Required for mode='sale' (or optional override for payout). */
  locationId?: string | null;
  /** Required for mode='sale' | 'issue' | 'stock_transaction'. */
  projectId?: string | null;
  orgId?: string | null;
  transactionId?: string | null;
}

export interface MediaFolderBuildArgs {
  orgId: string;
  projectId: string;
  locationId: string;
  feature: string[];
}
